import { useState, useEffect } from "react";
import { Box, Button, Flex, HStack, Image, Input, Stack, Text } from "@chakra-ui/react";
import { ArrowLeftRight, ArrowRight, Lock, ShieldCheck, User, Users } from "lucide-react";
import { Field } from "../ui/field";
import { PasswordInput } from "../ui/password-input";
import { InputGroup } from "../ui/input-group";
import { useDispatch } from "react-redux";
import { login } from "../../Redux/Action/auth/loginAction";
import { BRAND_LOGO, BRAND_NAME, getBrandLogo } from "../../Utils/brandLogo";
import { getWhitelabels } from "../../Redux/Action";
import { getLocalStorageItem } from "../../Utils/Helper"
import { useTheme } from "next-themes";

export const LoginForm = () => {
  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [isDisabled, setIsDisabled] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const { email, password } = formData;
    let hasError = false;

    if (!email) {
      setErrors((prev) => ({ ...prev, email: "Username is required" }));
      hasError = true;
    }
    if (!password) {
      setErrors((prev) => ({ ...prev, password: "Password is required" }));
      hasError = true;
    }

    const whitelabelLocalStorage = getLocalStorageItem("whitelabelData");
    if (whitelabelLocalStorage !== null) {
      formData.whiteLabelId = whitelabelLocalStorage._id;
    }

    if (!hasError) {
      let payload = formData;

      dispatch(
        login({
          payload,
          callback: (data) => {
            if (data.meta.code === 200) {
              setIsDisabled(false);
            } else {
              setIsDisabled(false);
            }
          },
        })
      );
    }
  };

  useEffect(() => {
    const { email, password } = formData;
    if (email && password && !errors.email && !errors.password) {
      setIsDisabled(false);
    }
  }, [formData, errors]);

  return (
    <Flex minHeight="100vh" width="100%" bg="bg.canvas">
      {/* Brand panel (desktop only) */}
      <Flex
        display={{ base: "none", lg: "flex" }}
        width="46%"
        maxWidth="640px"
        direction="column"
        justify="space-between"
        position="relative"
        overflow="hidden"
        px={{ lg: 12, xl: 16 }}
        py={12}
        color="white"
        bg="linear-gradient(160deg, #0b1437 0%, #070b1f 55%, #04060f 100%)"
      >
        {/* Decorative grid + glows */}
        <Box
          position="absolute"
          inset="0"
          pointerEvents="none"
          opacity={0.5}
          backgroundImage="linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)"
          backgroundSize="44px 44px"
          css={{ maskImage: "radial-gradient(ellipse at 30% 40%, black 20%, transparent 75%)" }}
        />
        <Box position="absolute" top="-160px" right="-120px" width="420px" height="420px" borderRadius="full" bg="radial-gradient(circle, rgba(59,102,245,0.35) 0%, transparent 70%)" pointerEvents="none" />
        <Box position="absolute" bottom="-180px" left="-140px" width="460px" height="460px" borderRadius="full" bg="radial-gradient(circle, rgba(34,211,238,0.18) 0%, transparent 70%)" pointerEvents="none" />

        {/* The brand panel is always dark, so it always takes the white-wordmark logo */}
        <Box position="relative">
          <Image src={BRAND_LOGO.onDark} alt={BRAND_NAME} h={{ lg: "72px", xl: "84px" }} w="auto" objectFit="contain" />
        </Box>

        <Stack position="relative" gap={8} maxWidth="440px">
          <Stack gap={4}>
            <Text fontSize="xs" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="#7dd3fc">
              Admin Console
            </Text>
            <Text as="h1" fontSize={{ lg: "3xl", xl: "4xl" }} fontWeight="700" lineHeight="1.15" letterSpacing="-0.02em">
              Everything you need to run your platform, in one place.
            </Text>
            <Text fontSize="md" color="whiteAlpha.700" lineHeight="1.6">
              Manage clients, review KYC and keep every transaction moving from a single, secure workspace.
            </Text>
          </Stack>

          <Stack gap={4}>
            {[
              { icon: Users, title: "Client & KYC management", text: "Onboard, verify and support your users." },
              { icon: ArrowLeftRight, title: "Funds & transactions", text: "Track deposits, withdrawals and transfers." },
              { icon: ShieldCheck, title: "Secure access", text: "Restricted to authorized administrators." },
            ].map(({ icon: FeatureIcon, title, text }) => (
              <HStack key={title} gap={4} align="flex-start">
                <Flex
                  width="40px"
                  height="40px"
                  flexShrink={0}
                  align="center"
                  justify="center"
                  borderRadius="10px"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  color="#7dd3fc"
                >
                  <FeatureIcon size={18} />
                </Flex>
                <Box>
                  <Text fontSize="sm" fontWeight="600">{title}</Text>
                  <Text fontSize="sm" color="whiteAlpha.600">{text}</Text>
                </Box>
              </HStack>
            ))}
          </Stack>
        </Stack>

        <Text position="relative" fontSize="xs" color="whiteAlpha.500">
          © {new Date().getFullYear()} Admin Console
        </Text>
      </Flex>

      {/* Sign-in side */}
      <Flex flex="1" align="center" justify="center" px={{ base: 5, sm: 8 }} py={{ base: 10, md: 12 }}>
        <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: "420px" }}>
          <Stack
            gap="5"
            align="stretch"
            width="100%"
            bg="bg.surface"
            border="1px solid"
            borderColor="border.subtle"
            borderRadius="2xl"
            boxShadow="pop"
            padding={{ base: "6", md: "10" }}
          >
            {/* Logo for mobile/tablet (the brand panel is hidden there) */}
            <Image
              src={getBrandLogo(resolvedTheme)}
              alt={BRAND_NAME}
              h={{ base: "56px", sm: "64px" }}
              w="auto"
              maxW="100%"
              objectFit="contain"
              alignSelf="center"
              display={{ base: "block", lg: "none" }}
            />

            <Stack gap={3}>
              <HStack
                gap={1.5}
                alignSelf="flex-start"
                px={2.5}
                py={1}
                borderRadius="full"
                bg="brand.subtle"
                color="brand.fg"
                fontSize="xs"
                fontWeight="600"
              >
                <ShieldCheck size={14} />
                <Text>Admin Console</Text>
              </HStack>
              <Box>
                <Text fontSize="2xl" fontWeight="700" color="fg.default" letterSpacing="-0.02em">
                  Welcome back
                </Text>
                <Text fontSize="sm" color="fg.muted" mt={1}>
                  Sign in with your admin credentials to continue.
                </Text>
              </Box>
            </Stack>

            <Field label="Email/Username" invalid={!!errors.email} errorText={errors.email}>
              <InputGroup width="100%" startElement={<User size={16} color="#98a2b3" />}>
                <Input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email or username"
                  height="44px"
                  borderRadius="10px"
                />
              </InputGroup>
            </Field>

            <Field
              label="Password"
              invalid={!!errors.password}
              errorText={errors.password}
            >
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                height="44px"
                borderRadius="10px"
                rootProps={{ startElement: <Lock size={16} color="#98a2b3" /> }}
              />
            </Field>

            <Button
              type="submit"
              disabled={isDisabled}
              width="100%"
              height="44px"
              mt={1}
              borderRadius="10px"
              fontWeight="600"
              bg="brand.600"
              color="white"
              _hover={{ bg: "brand.700" }}
            >
              Sign in
              <ArrowRight size={16} />
            </Button>

            <HStack justify="center" gap={1.5} color="fg.subtle" fontSize="xs">
              <Lock size={12} />
              <Text>Authorized personnel only</Text>
            </HStack>
          </Stack>
        </form>
      </Flex>
    </Flex>
  );
};
